/**
 * @file 到達度モデル (attainment model) の型。
 *
 * @what: `attainment_v1.yaml` を parse した後の in-memory 表現。
 * @why: YAML は人が編む単元集・到達水準の正本で、UI と（将来の）逆算ロジックは
 *   この型だけを見る。YAML の構造変更は parse.ts の検証と tests/study の
 *   テストで受け止め、UI に生 YAML の形を漏らさない。
 */

export type LevelId = "experience" | "introduce" | "practice" | "consolidate";

export const LEVEL_IDS: readonly LevelId[] = ["experience", "introduce", "practice", "consolidate"];

export interface Level {
  id: LevelId;
  label: string;
  description: string;
}

export type PreparationKind = "material" | "tool" | "environment";

export const PREPARATION_KINDS: readonly PreparationKind[] = ["material", "tool", "environment"];

/** このステップで用意するもの (カタログ側)。教材は種類 (一般語) で持ち、実物は家庭側で登録する。 */
export interface Preparation {
  /** 全ステップ横断で一意。家庭側のチェック状態 (prep_states) のキー。 */
  id: string;
  label: string;
  kind: PreparationKind;
  note?: string;
}

/**
 * 終わりまでにできていてほしいこと 1 件。units は関係する単元 (取り込んだ資料の出題数を
 * 横に出すのに使う)。単元に対応しないもの (行事の経験・確認の結果) は空。
 */
/**
 * 「終わりまでにできていてほしいこと」1 項目。
 *
 * @what: チェックリストの 1 行。id は家庭側の達成チェック (plan.exit_states) のキーで、
 *   全ステップをまたいで一意 (parse が検証する)。
 * @why: 達成日はサーバが id 単位で持つので、id が変わると過去の達成記録と切れる。
 * @failure: id の変更・削除は「達成済みが未達成に見える」形で現れる。文言 (text) の
 *   手直しは自由だが、id は据え置く。項目自体を消すときは記録が消えてよいか判断してから。
 */
export interface StageExit {
  id: string;
  text: string;
  units: string[];
}

/**
 * 大切にすること 1 件。lead は太字の見出し (無ければ本文だけ)。items は列挙 (「・」で
 * つないだ文にすると箇条書きの点と見分けがつかないので、別の要素として並べる。代表 2026-08-24)。
 */
export interface StageFocus {
  lead?: string;
  text?: string;
  items?: string[];
}

export interface Stage {
  id: string;
  label: string;
  period: string;
  grade: string;
  /** 大切にすること。箇条書きで出す (長い段落にしない。代表 2026-08-24)。 */
  focus: StageFocus[];
  exit: StageExit[];
  preparations: Preparation[];
}

export interface Domain {
  id: string;
  label: string;
  aim: string;
}

/** 考査の種類 (ペーパー / 個別 / 絵画・制作 / 行動観察 / 運動 / 面接)。 */
export interface Assessment {
  id: string;
  label: string;
}

export interface Unit {
  id: string;
  label: string;
  domain: string;
  /** `分野/単元` 形式。vocabulary に存在する参照のみ許可。省略可 (= 語彙追加候補)。 */
  taxonomy: string[];
  /** stage id → その段で目指す水準 */
  expected: Record<string, LevelId>;
  /** 考査の種類 (assessments カタログの id)。空 = 考査に直接は出ない土台。 */
  assessments: string[];
  /** stage id → 補足 (段ごとの進め方・ゲート) */
  notes: Record<string, string>;
  /**
   * その水準と時期をなぜそう置いたか。取り込んだ資料の出題数を根拠にした単元にだけ入る。
   * 出題数そのものは資料から動的に数えるので、ここには数えれば分かることでなく判断を書く。
   */
  basis?: string;
}

export interface CalibrationPoint {
  id: string;
  label: string;
  stage: string;
  timing: string;
  /** 学年の中の月 (1〜12)。時期が月で決まるものだけ持つ (見取り図の時間軸に印を置く位置)。 */
  month?: number;
  measures: string;
  method: string;
  criterion: string;
  outcome?: string;
  notes: string[];
}

export interface AttainmentModel {
  version: number;
  id: string;
  title: string;
  scope: string;
  principles: string[];
  levels: Level[];
  stages: Stage[];
  domains: Domain[];
  assessments: Assessment[];
  units: Unit[];
  calibrationPoints: CalibrationPoint[];
  /** 分野 → 単元語彙 */
  vocabulary: Record<string, string[]>;
}
